/**
 * 이상한 방 - 구획 이름 하나 (`room.<zoneId>` 접두사의 유일한 원본)
 *
 * 근거 문서: `src/i18n/GLOSSARY.md` 1.5절(구획 / zone - '방'은 원래 방과 겹치므로 구획이다) · 4.2절(조립하는 키)
 *            `이상한방-상태모델-v0.1.md` 11.2절(클라이언트가 가진 것 - 구획 id는 오지만 문구는 사전의 것)
 *            로드맵 M11(각자 방 - 화면 여러 곳이 같은 구획 이름을 말한다)
 *
 * M11 전에는 이 조회가 `game/reveal.ts` 안의 사적 함수 하나였다(리빌 라벨만 구획 이름을 썼다). 이제 같은 이름을
 * **다섯 화면**이 말한다 - 바꾸기 띠 · HUD 칩 · 바꾸기 시작 자막 · 리빌 이름표 · 결과 화면. 접두사(`room.`)를
 * 다섯 자리에 적으면 구획 id의 이름 규칙이 바뀌는 날 한 자리만 남는다. 그래서 접두사를 아는 파일이 여기 하나다.
 *
 * ## 사전이 유일한 출처다 - 슬러그로 떨어지지 않는다
 *
 * 평면도의 `Room.name`은 에디터·디버그용 키 문자열이지 화면 문구가 아니다(`core/floorPlanModel.Room` 주석).
 * 사전에 없는 구획은 이름을 **생략한다**(`undefined`) - 폴백을 두면 화면에 'bedroom-guest'가 뜨고, 이름 한 조각이
 * 빠진 문장이 번역되지 않은 슬러그보다 낫다. 키가 빠지는 것은 데이터 오류이고 `i18n/index.test.ts`가 모든 평면도의
 * 구획 id에 ko/en 키가 있는지 본다.
 *
 * `i18n/`은 최하층이라 `core`조차 보지 않는다 - 받는 것은 문자열 id 하나다.
 */

import { hasKey, t } from './index'

/**
 * 구획 id → 지금 언어의 이름. 사전에 없거나 id가 없으면 `undefined`다 - 부르는 쪽이 이름 없는 화면을 그린다
 * (빈 문자열을 돌려주면 "이름이 있는데 비었다"와 구별되지 않는다).
 *
 * 접두사를 상수로 빼지 않고 템플릿에 그대로 둔 이유: `i18n/index.test.ts`의 미참조 키 검사가 `` `room.${'${'}…}` `` 꼴의
 * **템플릿 접두사**를 읽어 "이 접두사의 키들은 코드가 부른다"고 판단한다. 상수 뒤에 숨기면 사전의 구획 이름 열두 개가
 * 전부 죽은 키로 잡힌다 - 검사가 읽을 수 있는 모양으로 적는 것이 이 한 줄의 계약이다.
 */
export function zoneName(zoneId: string | null | undefined): string | undefined {
  if (zoneId === null || zoneId === undefined) return undefined
  const key = `room.${zoneId}`
  return hasKey(key) ? t(key) : undefined
}
